import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "rc-cm-bottom-panels" }

import {
  ref, shallowRef, computed, onMounted, onBeforeUnmount, watch, useAttrs
} from 'vue';
import type { Extension } from '@codemirror/state';
import { EditorState, Compartment } from '@codemirror/state';
import {
  EditorView,
  drawSelection,
  dropCursor,
  highlightActiveLineGutter,
  highlightSpecialChars,
  rectangularSelection,
  crosshairCursor,
  lineNumbers as cmLineNumbers
} from '@codemirror/view';
import { history } from '@codemirror/commands';
import { searchPanelOpen } from '@codemirror/search';
import {
  indentOnInput,
  syntaxHighlighting,
  defaultHighlightStyle,
  bracketMatching,
  foldGutter as cmFoldGutter
} from '@codemirror/language';
import { closeBrackets, autocompletion } from '@codemirror/autocomplete';
import { linter as cmLinter, lintGutter, type LintSource } from '@codemirror/lint';
import RcButton from '@components/RcButton/RcButton.vue';
import { getLanguageExtension } from './extensions/syntax';
import { getKeymapExtension } from './extensions/keymaps';
import { buildFoldExtension } from './extensions/fold';
import { bottomPanelsExtension } from './extensions/panels';
import { openSearchPanelQuietly } from './extensions/searchPanel';
import { rancherInputTheme, rancherTheme } from './extensions/theme';
import type { RcCodeMirrorKeymap, RcCodeMirrorProps, RcCodeMirrorTheme, RcCodeMirrorVariant } from './types';

const ESCAPE_HINT = 'Press Escape, then Tab to leave the editor';

export default /*@__PURE__*/_defineComponent({
  ...{ inheritAttrs: false },
  __name: 'RcCodeMirror',
  props: {
    modelValue: { default: '' },
    language: { default: undefined },
    keymap: { default: undefined },
    theme: { default: 'rancher' },
    variant: { default: 'editor' },
    readOnly: { type: Boolean, default: false },
    lineNumbers: { type: Boolean, default: true },
    foldGutter: { type: Boolean, default: true },
    lineWrapping: { type: Boolean, default: false },
    extensions: { default: undefined },
    foldOptions: { default: undefined },
    linter: { type: Function, default: undefined },
    keymapIndicator: { type: Boolean, default: false },
    searchOpen: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "change", "focus", "blur", "ready"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

/**
 * A code editor built on CodeMirror 6 with YAML and JSON support, swappable
 * keymaps (default, sublime, vim, emacs) and configurable code folding. Every prop can
 * change after mount without rebuilding the editor.
 *
 * Example:
 *
 * <RcCodeMirror v-model="yaml" language="yaml" />
 *
 * <RcCodeMirror v-model="value" variant="input" />
 *
 * <RcCodeMirror
 *   v-model="yaml"
 *   language="yaml"
 *   keymap="vim"
 *   :read-only="false"
 *   :line-wrapping="true"
 *   :fold-options="{ strategy: 'indent' }"
 *   :extensions="[foldByYamlPath('metadata.labels')]"
 *   @ready="(view) => foldYamlPath(view, 'metadata.labels')"
 * />
 *
 * The underlying EditorView is emitted with `ready` and exposed as `view` on
 * the component ref for anything the props do not cover.
 *
 * ARIA attributes (e.g. `aria-label`, `aria-labelledby`) and `tabindex` are
 * forwarded to the editor's textbox. Give every instance an accessible name.
 * In the editor variant, Tab indents at the cursor (or indents the selected
 * lines) and Shift-Tab unindents with the default keymap and in Vim Insert mode.
 * Emacs Tab reindents the line. In Vim Normal mode Tab moves through the jump
 * list and Shift-Tab does nothing. Press Escape then Tab to move focus out.
 * In a read-only editor Tab and Shift-Tab move focus with every keymap.
 */


const props = __props;

const emit = __emit;

const attrs = useAttrs();
const container = ref<HTMLDivElement>();
const bottomPanels = ref<HTMLDivElement>();
const view = shallowRef<EditorView>();
const isEditorFocused = ref(false);
const isSearchPanelOpen = ref(false);
let initialState: EditorState | undefined;
const escapeHint = computed(() => view.value?.state.phrase(ESCAPE_HINT) ?? ESCAPE_HINT);

const KEYMAP_NAMES: Partial<Record<RcCodeMirrorKeymap, string>> = { vim: 'Vim', emacs: 'Emacs' };
const isKeymapIndicatorDismissed = ref(false);
const keymapName = computed(() => (props.keymap ? KEYMAP_NAMES[props.keymap] : undefined));
// Waits for the view, whose phrases translate the indicator's text
const showKeymapIndicator = computed(() => !!view.value && props.keymapIndicator && props.variant !== 'input' && !!keymapName.value && !isKeymapIndicatorDismissed.value && !isSearchPanelOpen.value);

// `$` is replaced with the keymap name, so translations can place it anywhere
function keymapPhrase(phrase: string): string {
  const state = view.value?.state;

  return state && keymapName.value ? state.phrase(phrase, state.phrase(keymapName.value)) : '';
}

const keymapIndicatorTooltip = computed(() => keymapPhrase('Key mapping: $'));
const keymapIndicatorLabel = computed(() => keymapPhrase('Hide key mapping: $'));

// The indicator is removed as it is selected, so give focus to the editor rather than losing it
function dismissKeymapIndicator(): void {
  isKeymapIndicatorDismissed.value = true;
  view.value?.focus();
}

function isEditorAttribute(name: string): boolean {
  return name.startsWith('aria-') || name.toLowerCase() === 'tabindex';
}

// CodeMirror renders the textbox inside the container, so its ARIA attributes and tab order
// belong there. Everything else still falls through to the container.
const containerAttrs = computed(() => Object.fromEntries(
  Object.entries(attrs).filter(([name]) => !isEditorAttribute(name))
));

function editorAttributes(): Record<string, string> {
  const attributes = Object.fromEntries(
    Object.entries(attrs)
      .filter(([name, value]) => isEditorAttribute(name) && value !== undefined && value !== null)
      .map(([name, value]) => [name.toLowerCase() === 'tabindex' ? 'tabindex' : name, String(value)])
  );

  if (props.readOnly && attributes.tabindex === undefined) {
    attributes.tabindex = '0';
  }

  return attributes;
}

// Compartments for hot-swappable extensions
const languageCompartment = new Compartment();
const keymapCompartment = new Compartment();
const themeCompartment = new Compartment();
const readOnlyCompartment = new Compartment();
const lineNumbersCompartment = new Compartment();
const lineWrappingCompartment = new Compartment();
const foldGutterCompartment = new Compartment();
const lintCompartment = new Compartment();
const contentAttributesCompartment = new Compartment();

function getThemeExtension(theme?: RcCodeMirrorTheme, variant?: RcCodeMirrorVariant): Extension {
  if (theme === 'rancher') {
    return variant === 'input' ? rancherInputTheme : rancherTheme;
  }

  return [];
}

function getLineNumbersExtension(show: boolean): Extension {
  return show ? cmLineNumbers() : [];
}

// The input variant has no gutters
function showLineNumbers(): boolean {
  return props.variant !== 'input' && (props.lineNumbers ?? true);
}

function foldMarkerDOM(open: boolean): HTMLElement {
  const span = document.createElement('span');
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const triangle = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  const phrase = open ? 'Fold line' : 'Unfold line';

  span.className = 'rc-cm-fold-marker';
  span.title = (view.value?.state ?? initialState)?.phrase(phrase) ?? phrase;
  icon.setAttribute('viewBox', '0 0 10 10');
  icon.setAttribute('aria-hidden', 'true');
  triangle.setAttribute('d', open ? 'M2.5 3 7.5 3 5 7.5z' : 'M3 2.5 7.5 5 3 7.5z');
  icon.appendChild(triangle);
  span.appendChild(icon);

  return span;
}

function getFoldGutterExtension(show: boolean): Extension {
  return show ? cmFoldGutter({ markerDOM: foldMarkerDOM }) : [];
}

// The input variant has no gutters. Folding itself stays enabled without the gutter, so folds
// can still be made programmatically (e.g. foldYamlPath) and from the keyboard
function showFoldGutter(): boolean {
  return props.variant !== 'input' && (props.foldGutter ?? true);
}

// The input variant has no gutters, so its problems are only underlined
function getLintExtension(source: LintSource | undefined, variant: RcCodeMirrorVariant): Extension {
  if (!source) {
    return [];
  }

  return variant === 'input' ? cmLinter(source) : [cmLinter(source), lintGutter()];
}

// The input variant always wraps, like a textarea
function wrapLines(): boolean {
  return props.variant === 'input' || (props.lineWrapping ?? false);
}

// editable only stops the content being contenteditable, readOnly stops commands (e.g. Enter
// from a keymap) changing the document, so both are needed
function getReadOnlyExtension(readOnly: boolean): Extension {
  return [EditorState.readOnly.of(readOnly), EditorView.editable.of(!readOnly)];
}

function getLineWrappingExtension(wrap: boolean): Extension {
  return wrap ? EditorView.lineWrapping : [];
}

function getContentAttributesExtension(attributes: Record<string, string>): Extension {
  return EditorView.contentAttributes.of(attributes);
}

function handleFocusIn(event: FocusEvent): void {
  if (event.target === view.value?.contentDOM) {
    isEditorFocused.value = true;
  }
}

function handleFocusOut(event: FocusEvent): void {
  if (event.target === view.value?.contentDOM) {
    isEditorFocused.value = false;
  }
}

function handleEditorKeydown(event: KeyboardEvent): void {
  const editor = view.value;

  if (editor && event.code === 'Escape' && !event.shiftKey && event.target === editor.contentDOM) {
    editor.setTabFocusMode(2000);
  }
}

// Escape belongs to the editor (Vim uses it to leave Insert mode), so it must not also reach page
// handlers such as a modal closing on Escape. This runs after CodeMirror has handled the key. Escape in the
// search panel removes the panel before the event bubbles here, so check the path it was dispatched along.
function stopEditorEscape(event: KeyboardEvent): void {
  const editor = view.value?.dom;

  if (event.code === 'Escape' && editor && event.composedPath().includes(editor)) {
    event.stopPropagation();
  }
}

onMounted(() => {
  if (!container.value) {
    return;
  }

  const updateListener = EditorView.updateListener.of((update) => {
    isSearchPanelOpen.value = searchPanelOpen(update.state);
    if (update.docChanged) {
      const value = update.state.doc.toString();

      emit('update:modelValue', value);
      emit('change', value);
    }
    if (update.focusChanged) {
      if (update.view.hasFocus) {
        emit('focus', update.view);
      } else {
        emit('blur', update.view);
      }
    }
  });

  const state = EditorState.create({
    doc:        props.modelValue ?? '',
    extensions: [
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      bracketMatching(),
      closeBrackets(),
      rectangularSelection(),
      crosshairCursor(),
      highlightActiveLineGutter(),
      highlightSpecialChars(),
      autocompletion(),
      buildFoldExtension(props.foldOptions),
      languageCompartment.of(getLanguageExtension(props.language)),
      keymapCompartment.of(getKeymapExtension(props.keymap, props.variant)),
      themeCompartment.of(getThemeExtension(props.theme, props.variant)),
      // Gutters are shown in this order
      lineNumbersCompartment.of(getLineNumbersExtension(showLineNumbers())),
      lintCompartment.of(getLintExtension(props.linter, props.variant)),
      foldGutterCompartment.of(getFoldGutterExtension(showFoldGutter())),
      lineWrappingCompartment.of(getLineWrappingExtension(wrapLines())),
      readOnlyCompartment.of(getReadOnlyExtension(props.readOnly ?? false)),
      contentAttributesCompartment.of(getContentAttributesExtension(editorAttributes())),
      updateListener,
      ...(bottomPanels.value ? [bottomPanelsExtension(bottomPanels.value)] : []),
      ...(props.extensions ?? [])
    ]
  });

  // The fold gutter renders its initial markers before view.value is assigned.
  initialState = state;
  const editorView = new EditorView({
    state,
    parent: container.value
  });

  view.value = editorView;
  if (props.searchOpen && props.variant !== 'input') {
    openSearchPanelQuietly(editorView);
  }
  emit('ready', editorView);
});

onBeforeUnmount(() => {
  view.value?.destroy();
});

// External modelValue changes → dispatch to editor
watch(
  () => props.modelValue,
  (newVal) => {
    const v = view.value;

    if (!v) {
      return;
    }
    const current = v.state.doc.toString();

    if (newVal === current) {
      return;
    }

    v.dispatch({
      changes: {
        from:   0,
        to:     v.state.doc.length,
        insert: newVal ?? ''
      }
    });
  }
);

// Hot-swap language
watch(
  () => props.language,
  (lang) => {
    view.value?.dispatch({ effects: languageCompartment.reconfigure(getLanguageExtension(lang)) });
  }
);

// Hot-swap keymap and Tab behavior when switching between editor and input variants
watch(
  () => [props.keymap, props.variant] as const,
  ([km, variant]) => {
    view.value?.dispatch({ effects: keymapCompartment.reconfigure(getKeymapExtension(km, variant)) });
  }
);

// Hot-swap theme
watch(
  () => [props.theme, props.variant] as const,
  ([theme, variant]) => {
    view.value?.dispatch({ effects: themeCompartment.reconfigure(getThemeExtension(theme, variant)) });
  }
);

// Hot-swap readOnly
watch(
  () => props.readOnly,
  (ro) => {
    view.value?.dispatch({ effects: readOnlyCompartment.reconfigure(getReadOnlyExtension(ro ?? false)) });
  }
);

// Hot-swap lineNumbers
watch(
  () => showLineNumbers(),
  (show) => {
    view.value?.dispatch({ effects: lineNumbersCompartment.reconfigure(getLineNumbersExtension(show)) });
  }
);

// Hot-swap linter
watch(
  () => [props.linter, props.variant] as const,
  ([source, variant]) => {
    view.value?.dispatch({ effects: lintCompartment.reconfigure(getLintExtension(source, variant)) });
  }
);

// Hot-swap foldGutter
watch(
  () => showFoldGutter(),
  (show) => {
    view.value?.dispatch({ effects: foldGutterCompartment.reconfigure(getFoldGutterExtension(show)) });
  }
);

// Hot-swap lineWrapping
watch(
  () => wrapLines(),
  (wrap) => {
    view.value?.dispatch({ effects: lineWrappingCompartment.reconfigure(getLineWrappingExtension(wrap)) });
  }
);

// Hot-swap editor attributes, including the read-only tab stop
watch(
  editorAttributes,
  (attributes) => {
    view.value?.dispatch({ effects: contentAttributesCompartment.reconfigure(getContentAttributesExtension(attributes)) });
  }
);

__expose({ view });

return (_ctx: any,_cache: any) => {
  const _directive_clean_tooltip = _resolveDirective("clean-tooltip")!

  return (_openBlock(), _createElementBlock("div", _mergeProps(containerAttrs.value, {
    ref_key: "container",
    ref: container,
    class: ["rc-code-mirror", `rc-code-mirror--${ __props.variant }`],
    onKeydownCapture: handleEditorKeydown,
    onKeydown: stopEditorEscape,
    onFocusin: handleFocusIn,
    onFocusout: handleFocusOut
  }), [
    _withDirectives(_createElementVNode("span", {
      class: "rc-cm-escape-hint",
      role: "alert"
    }, _toDisplayString(escapeHint.value), 513), [
      [_vShow, isEditorFocused.value && __props.variant !== 'input' && !__props.readOnly]
    ]),
    _cache[1] || (_cache[1] = _createTextVNode()),
    (showKeymapIndicator.value)
      ? _withDirectives((_openBlock(), _createBlock(RcButton, {
          key: 0,
          type: "button",
          variant: "ghost",
          class: "rc-cm-keymap-indicator",
          "data-testid": "code-mirror-keymap",
          "aria-label": keymapIndicatorLabel.value,
          onClick: dismissKeymapIndicator
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createElementVNode("i", {
              class: "icon icon-keyboard rc-cm-keymap-icon",
              "aria-hidden": "true"
            }, null, -1),
            _createTextVNode(),
            _createElementVNode("i", {
              class: "icon icon-close icon-sm rc-cm-keymap-close",
              "aria-hidden": "true"
            }, null, -1)
          ]))]),
          _: 1
        }, 8, ["aria-label"])), [
          [_directive_clean_tooltip, keymapIndicatorTooltip.value]
        ])
      : _createCommentVNode("", true),
    _cache[2] || (_cache[2] = _createTextVNode()),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", {
        ref_key: "bottomPanels",
        ref: bottomPanels
      }, null, 512)
    ])
  ], 16))
}
}

})